import { useCallback } from 'react'

import styled from '@emotion/styled'
import { useTranslation } from 'next-i18next'

import type { ArcadeIcon } from '@arcade/web/icons/react'
import { DocumentFillIcon, LinkOutIcon } from '@arcade/web/icons/react'
import { spacing, Text } from '@cash-design-system/react'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { CardItem, CardList, LinkAndAnchorStyles } from '@cash-web/support/ui-components'
import { useAnalyticsContext } from '@cash-web/support/utils'
import type {
  CustomerSupportAccessOpenNodeSelectedType,
  CustomerSupportAccessOpenNodeTrigger,
} from '@squareup/customer-data-events-web'
import { CustomerSupportAccessOpenNode, WebInteractClick } from '@squareup/customer-data-events-web'

import type { ArticleListing } from '../../../types'
import Link from '../../components/RegionalisedLink'

const StyledHeader = styled(Text)`
  margin-bottom: ${spacing.m};
`

const StyledAnchor = styled(Link)`
  ${LinkAndAnchorStyles}
`

interface ArticleListProps {
  tabletColumns?: 1 | 2
  trigger: CustomerSupportAccessOpenNodeTrigger
  data: ArticleListing[]
  title?: string
  analyticsType?: CustomerSupportAccessOpenNodeSelectedType
}

const ArticleList = ({ data, tabletColumns = 1, title, trigger, analyticsType = 'SUGGESTED' }: ArticleListProps) => {
  const { t } = useTranslation()
  const logCdfEvent = useLogCashWebCdfEvent()
  const { viewToken } = useAnalyticsContext()

  const trackEvent = useCallback(
    (article: ArticleListing & { index: number }) => {
      logCdfEvent(
        WebInteractClick({
          pageName: article.title,
          componentName: 'A',
          componentType: 'LINK_CLICK',
        })
      )

      if ('token' in article) {
        logCdfEvent(
          CustomerSupportAccessOpenNode({
            trigger: trigger,
            viewToken: viewToken,
            selectedType: analyticsType,
            selectedTypeCount: data.length,
            selectedTypeIndex: article.index,
            nodeToken: article.token,
          })
        )
      }
    },
    [data, logCdfEvent, analyticsType, viewToken, trigger]
  )

  return (
    <>
      {title ? (
        <StyledHeader typography="tabTitle" color="textStandard" as="h2">
          {title}
        </StyledHeader>
      ) : undefined}
      <CardList
        divider
        columns={{ mobile: 1, tablet: tabletColumns }}
        data={data}
        render={article => (
          <StyledAnchor
            href={'token' in article ? `/${article.token}-${article.slug}` : article.url}
            target={'url' in article ? '_blank' : undefined}
            onClick={() => trackEvent(article)}
          >
            <CardItem
              // Keys are statically extracted in browse.ts/popularTopics.ts for Atlas
              // eslint-disable-next-line @nx/workspace-i18n-only-static-keys, @nx/workspace-i18n-require-context
              title={t(article.title ?? '')}
              isClickable
              icon={'token' in article ? (DocumentFillIcon as ArcadeIcon) : (LinkOutIcon as ArcadeIcon)}
            />
          </StyledAnchor>
        )}
      />
    </>
  )
}

export default ArticleList
